// TIER_KEYS — ordered const array; index = tier level (0=free … 4=vea)
export const TIER_KEYS = ['free', 'growth', 'agent', 'rep', 'vea'] as const;
export type TierKey = typeof TIER_KEYS[number];

export interface TierCapabilities {
  canRemoveBranding: boolean;
  canUseScraper: boolean;
  canUseKnowledgeBase: boolean;
  canUseGenerativeAI: boolean;
  canUseRAG: boolean;
  maxChannels: number;
  hasAnalytics: boolean;
  hasChatbotAnalytics: boolean;      // Agent+
  canUseAdvancedAnalytics: boolean;  // Rep+ (sparklines, avg msgs/conv)
  hasPrioritySupport: boolean;
  hasAdvancedScheduling: boolean;
  canCustomizeButtonColor: boolean;
  canCustomizeButtonSize: boolean;
  maxAIInteractions: number;         // 0 = unlimited (Vea)
  hasCSVExport: boolean;
  hasSalesPrompting: boolean;
  hasProductDesigner: boolean;
  hasVeaAnalytics: boolean;
  hasConversionSignals: boolean;     // all false at launch; flip Growth+ when CartSync ships
  hasDeviceTargeting: boolean;       // Growth+
  analyticsRetentionDays: number;
  price: number;
  annualPrice: number;
}

export const TIER_CONFIG: Record<TierKey, TierCapabilities> = {
  free: {
    canRemoveBranding: false,
    canUseScraper: false,
    canUseKnowledgeBase: false,
    canUseGenerativeAI: false,
    canUseRAG: false,
    maxChannels: 99,               // was 1 — no channel caps ever
    hasAnalytics: true,            // 7-day teaser
    hasChatbotAnalytics: false,
    canUseAdvancedAnalytics: false,
    hasPrioritySupport: false,
    hasAdvancedScheduling: true,   // was false — scheduling now free
    canCustomizeButtonColor: false,
    canCustomizeButtonSize: false,
    maxAIInteractions: 0,
    hasCSVExport: false,
    hasSalesPrompting: false,
    hasProductDesigner: false,
    hasVeaAnalytics: false,
    hasConversionSignals: false,
    hasDeviceTargeting: false,
    analyticsRetentionDays: 7,     // was 0
    price: 0,
    annualPrice: 0,
  },
  growth: {                        // was 'support' key
    canRemoveBranding: true,
    canUseScraper: false,
    canUseKnowledgeBase: false,
    canUseGenerativeAI: false,
    canUseRAG: false,
    maxChannels: 99,
    hasAnalytics: true,
    hasChatbotAnalytics: false,
    canUseAdvancedAnalytics: false,
    hasPrioritySupport: false,
    hasAdvancedScheduling: true,
    canCustomizeButtonColor: true,
    canCustomizeButtonSize: true,
    maxAIInteractions: 0,
    hasCSVExport: false,
    hasSalesPrompting: false,
    hasProductDesigner: false,
    hasVeaAnalytics: false,
    hasConversionSignals: false,   // flip true when CartSync sprint ships
    hasDeviceTargeting: true,
    analyticsRetentionDays: 90,    // was 0
    price: 12,
    annualPrice: 10,
  },
  agent: {
    canRemoveBranding: true,
    canUseScraper: true,
    canUseKnowledgeBase: true,
    canUseGenerativeAI: false,     // FTS/keyword only — no Gemini
    canUseRAG: false,
    maxChannels: 99,
    hasAnalytics: true,
    hasChatbotAnalytics: true,
    canUseAdvancedAnalytics: false,
    hasPrioritySupport: false,
    hasAdvancedScheduling: true,
    canCustomizeButtonColor: true,
    canCustomizeButtonSize: true,
    maxAIInteractions: 0,
    hasCSVExport: false,
    hasSalesPrompting: false,
    hasProductDesigner: false,
    hasVeaAnalytics: false,
    hasConversionSignals: false,
    hasDeviceTargeting: true,
    analyticsRetentionDays: 90,
    price: 39,                     // was 49
    annualPrice: 33,               // was 42
  },
  rep: {
    canRemoveBranding: true,
    canUseScraper: true,
    canUseKnowledgeBase: true,
    canUseGenerativeAI: true,
    canUseRAG: true,
    maxChannels: 99,
    hasAnalytics: true,
    hasChatbotAnalytics: true,
    canUseAdvancedAnalytics: true,
    hasPrioritySupport: true,
    hasAdvancedScheduling: true,
    canCustomizeButtonColor: true,
    canCustomizeButtonSize: true,
    maxAIInteractions: 5000,
    hasCSVExport: true,
    hasSalesPrompting: true,
    hasProductDesigner: false,
    hasVeaAnalytics: false,
    hasConversionSignals: false,
    hasDeviceTargeting: true,
    analyticsRetentionDays: 365,
    price: 99,                     // was 149
    annualPrice: 84,               // was 127
  },
  vea: {
    canRemoveBranding: true,
    canUseScraper: true,
    canUseKnowledgeBase: true,
    canUseGenerativeAI: true,
    canUseRAG: true,
    maxChannels: 99,
    hasAnalytics: true,
    hasChatbotAnalytics: true,
    canUseAdvancedAnalytics: true,
    hasPrioritySupport: true,
    hasAdvancedScheduling: true,
    canCustomizeButtonColor: true,
    canCustomizeButtonSize: true,
    maxAIInteractions: 0,          // unlimited
    hasCSVExport: true,
    hasSalesPrompting: true,
    hasProductDesigner: true,
    hasVeaAnalytics: true,
    hasConversionSignals: false,
    hasDeviceTargeting: true,
    analyticsRetentionDays: 730,
    price: 299,
    annualPrice: 254,
  },
};

export function normalizePlanTier(raw: string): TierKey {
  const lower = raw?.toLowerCase() || 'free';
  return TIER_KEYS.includes(lower as TierKey) ? (lower as TierKey) : 'free';
}

export function tierIndex(raw: string): number {
  return TIER_KEYS.indexOf(normalizePlanTier(raw));
}

export function getTierCapabilities(plan: string): TierCapabilities {
  return TIER_CONFIG[normalizePlanTier(plan)];
}
